import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { apiAxios as axios } from "../../utils/apiClient.js";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  FormatQuote,
  ShoppingBag,
  LocalOffer,
  Savings,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { url } from "../../Constant/TextConstant";
import RelatedProduct from "../RelatedProductScreen/RelatedProduct";
import Header from "../Header";
import Footer from "../Footer/Footer";
import { Helmet } from "react-helmet";
import { useUserLocation } from "../../utils/useUserLocation";

const SuccessScreenContainer = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #e4efe9 100%);
  min-height: 100vh;
  padding: 20px 0;
`;

const SuccessContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
`;

const MainContentWrapper = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 40px;

  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  max-width: 600px;

  @media (max-width: 992px) {
    max-width: 100%;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  background: #ffffff;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 150px;
  align-self: flex-start;
  border: 1px solid #e0e0e0;
`;

// Celebration animation
const celebrate = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const SuccessHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
  position: relative;
`;

const CelebrationEffect = styled.div`
  position: absolute;
  top: -20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 20px;
  z-index: 1;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  transition: opacity 0.5s ease;
`;

const CelebrationDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${(props) => props.color};
  animation: ${pulse} 1s infinite;
  animation-delay: ${(props) => props.delay || "0s"};
`;

const SuccessIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  box-shadow: 0 8px 25px rgba(76, 175, 80, 0.3);
  margin: 0 auto 20px;
  padding: 20px;
  position: relative;
  z-index: 2;
  animation: ${celebrate} 0.5s ease 3;
`;

const SuccessTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: #2e7d32;
  margin-bottom: 8px;
  text-align: center;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SuccessMessage = styled.p`
  font-size: 1.2rem;
  color: #616161;
  margin-bottom: 30px;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const CardContainer = styled.div`
  width: 100%;
  padding: 25px;
  background: ${(props) =>
    props.$bgColor || "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"};
  border-radius: 16px;
  border: none;
  margin-bottom: 25px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(props) => props.$color || "#1565c0"};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const CashbackAmount = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #0d47a1;
  margin: 15px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CardDescription = styled.p`
  font-size: 1rem;
  color: ${(props) => props.$color || "#1e88e5"};
  text-align: center;
  line-height: 1.6;
`;

const QuoteIcon = styled(FormatQuote)`
  color: ${(props) => props.$color || "#7b1fa2"};
  font-size: 30px !important;
`;

const QuoteText = styled.p`
  font-size: 1.2rem;
  font-style: italic;
  color: #4a148c;
  text-align: center;
  margin: 12px 0;
  line-height: 1.6;
  font-weight: 500;
`;

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  margin: 25px 0 15px;
  color: #2c3e50;
  position: relative;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(to right, #3498db, #9b59b6);
    border-radius: 4px;
  }
`;

const ProductItem = styled.div`
  display: flex;
  width: 100%;
  padding: 20px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
  transition: all 0.3s ease;
  border-left: 4px solid #3498db;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ProductImage = styled.img`
  width: 90px;
  height: 90px;
  object-fit: cover;
  border-radius: 10px;
  margin-right: 15px;

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

const ProductIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 90px;
  height: 90px;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  border-radius: 10px;
  margin-right: 15px;

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ProductName = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #2c3e50;
`;

const ProductPrice = styled.span`
  font-size: 1rem;
  color: #2c3e50;
  margin-bottom: 6px;
  font-weight: 500;
`;

const ProductStatus = styled.span`
  font-size: 0.9rem;
  color: #4caf50;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 5px;
`;

const ProductCashback = styled.span`
  font-size: 0.9rem;
  color: #2196f3;
  margin-top: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ContinueButton = styled.button`
  width: 100%;
  padding: 18px;
  background: linear-gradient(to right, #3498db, #2980b9);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 30px;
  transition: all 0.3s;
  box-shadow: 0 6px 15px rgba(52, 152, 219, 0.3);

  &:hover {
    background: linear-gradient(to right, #2980b9, #3498db);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(52, 152, 219, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const TotalAmount = styled.div`
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  border-radius: 12px;
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  font-weight: 700;
  font-size: 1.3rem;
  color: #2c3e50;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
`;

const OrderSummaryTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OrderSummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-size: 1rem;
`;

const OrderSummaryLabel = styled.span`
  color: #7f8c8d;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OrderSummaryValue = styled.span`
  font-weight: 600;
  color: #2c3e50;
`;

const OrderSummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px dashed #ddd;
  font-weight: 700;
  font-size: 1.3rem;
`;

const TotalSavings = styled.div`
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-radius: 12px;
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  font-weight: 700;
  font-size: 1.3rem;
  color: #2e7d32;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
`;

const SavingsBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-left: 10px;
`;

const DiscountPill = styled.div`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #e91e63 0%, #ad1457 100%);
  color: white;
  padding: 3px 10px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 8px;
`;

const SuccessScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;

  const baseAPIUrl = url.baseAPIUrl;
  const productImageStartingPath = url.productImageStartingPath;
  const [cashbackAmount, setCashbackAmount] = useState(0);
  const [productCashbacks, setProductCashbacks] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Replace the local location state with the hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  const [quote, setQuote] = useState(
    "Great things come to those who shop wisely!"
  );

  const quotes = [
    "Your purchase is planting seeds for future rewards!",
    "Smart shopping leads to happy savings!",
    "Every purchase brings you closer to your next reward!",
    "You're not just shopping, you're investing in rewards!",
  ];

  useEffect(() => {
    if (state?.products && state?.username) {
      fetchCashback();
    }
    selectRandomQuote();

    // Hide celebration after 5 seconds
    const celebrationTimer = setTimeout(() => {
      setShowCelebration(false);
    }, 5000);

    return () => clearTimeout(celebrationTimer);
  }, []);

  const selectRandomQuote = () => {
    const randomIndex = Math.floor(Math.random() * quotes.length);
    setQuote(quotes[randomIndex]);
  };

  const fetchCashback = async () => {
    const cashbacks = {};
    let totalCashback = 0;

    try {
      const requests = state.products.map((product) => {
        const encodedProductId = encodeURIComponent(product.ID.toString());
        const encodedUsername = encodeURIComponent(state.username);
        const requestUrl = `${baseAPIUrl}/api/reward/getCashbackForProduct?productId=${encodedProductId}&StoreMobile=${encodedUsername}`;

        return axios
          .get(requestUrl, { headers: { Accept: "application/json" } })
          .then((response) => ({
            productId: product.ID,
            cashback:
              (typeof response.data === "number" ? response.data : 0) *
              (product.Quantity || 1),
            perItemCashback:
              typeof response.data === "number" ? response.data : 0,
          }))
          .catch(() => ({
            productId: product.ID,
            cashback: (product.Cashback || 0) * (product.Quantity || 1),
            perItemCashback: product.Cashback || 0,
          }));
      });

      const results = await Promise.all(requests);

      results.forEach(({ productId, cashback, perItemCashback }) => {
        cashbacks[productId] = {
          totalCashback: cashback,
          perItemCashback: perItemCashback,
        };
        totalCashback += cashback;
      });

      setCashbackAmount(totalCashback);
      setProductCashbacks(cashbacks);
    } catch (error) {
      // Fallback: Use cashback values from product objects if available
      const fallback = {};
      let fallbackTotal = 0;
      state.products.forEach((product) => {
        const cb = (product.Cashback || 0) * (product.Quantity || 1);
        fallback[product.ID] = {
          totalCashback: cb,
          perItemCashback: product.Cashback || 0,
        };
        fallbackTotal += cb;
      });
      setProductCashbacks(fallback);
      setCashbackAmount(fallbackTotal);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueShopping = () => {
    navigate("/");
  };

  // Fixed subTotal calculation
  const calculateSubTotal = () => {
    if (!state?.products) return 0;

    // Use the subTotal from navigation state if available, otherwise calculate from products
    if (state.subTotal !== undefined) {
      return state.subTotal;
    }

    return state.products.reduce(
      (total, product) => total + (product.OurPrice * product.Quantity || 0),
      0
    );
  };

  const calculateTotalSavings = () => {
    if (!state?.products || !Array.isArray(state.products)) return 0;

    // Calculate total MRP (Maximum Retail Price)
    const totalMRP = state.products.reduce((total, product) => {
      const mrp = Number(product.Mrp) || 0;
      const quantity = Number(product.Quantity) || 1;
      return total + mrp * quantity;
    }, 0);

    // Calculate total amount actually paid
    const totalPaid = calculateSubTotal();

    // Calculate price savings (difference between MRP and paid price)
    const priceSavings = Math.max(0, totalMRP - totalPaid);

    // Add cashback to get total savings
    const totalSavings = priceSavings + (Number(cashbackAmount) || 0);

    return totalSavings;
  };

  // Calculate discount amount
  const calculateDiscount = () => {
    if (!state?.products || !Array.isArray(state.products)) return 0;

    // Use the discount from navigation state if available
    if (state.discount !== undefined) {
      return state.discount;
    }

    // Otherwise calculate discount from MRP vs OurPrice
    return state.products.reduce((total, product) => {
      const mrp = Number(product.Mrp) || 0;
      const ourPrice = Number(product.OurPrice) || 0;
      const quantity = Number(product.Quantity) || 1;
      const productDiscount = Math.max(0, (mrp - ourPrice) * quantity);
      return total + productDiscount;
    }, 0);
  };

  // At the start of SuccessScreen component:
  if (!state?.products || !Array.isArray(state.products)) {
    return (
      <>
        <Helmet>
          <title>Order Information Missing | Our Store</title>
          <meta
            name="description"
            content="We couldn't retrieve your order details. Please check your order history."
          />
        </Helmet>
        <SuccessScreenContainer>
          <SuccessContent>
            <SuccessTitle>Order Information Missing</SuccessTitle>
            <SuccessMessage>
              We couldn't retrieve your order details. Please check your order
              history.
            </SuccessMessage>
            <ContinueButton onClick={() => navigate("/")}>
              Return Home
            </ContinueButton>
          </SuccessContent>
        </SuccessScreenContainer>
      </>
    );
  }

  const subTotal = calculateSubTotal();
  const discountAmount = calculateDiscount();
  const totalSavings = calculateTotalSavings();

  return (
    <>
      <Helmet>
        <title>
          {state?.products?.length > 0
            ? `Order Success - ${state.products[0].ProductName}`
            : "Order Successfully Placed"}
        </title>
        <meta
          name="description"
          content={
            state?.products?.length > 0
              ? `Your order for ${state.products[0].ProductName} and ${
                  state.products.length - 1
                } other items has been successfully placed.`
              : "Your order has been successfully placed. Thank you for shopping with us."
          }
        />
      </Helmet>
      <Header
        userLocation={currentLocation}
        setShowModal={setShowModal}
        setUserLocation={updateLocation}
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <SuccessScreenContainer>
        <SuccessContent>
          <SuccessHeader>
            <CelebrationEffect $show={showCelebration}>
              <CelebrationDot color="#4CAF50" delay="0s" />
              <CelebrationDot color="#2196F3" delay="0.2s" />
              <CelebrationDot color="#FF9800" delay="0.4s" />
              <CelebrationDot color="#E91E63" delay="0.6s" />
              <CelebrationDot color="#9C27B0" delay="0.8s" />
            </CelebrationEffect>
            <SuccessIconContainer>
              <CheckCircle style={{ color: "white", fontSize: 80 }} />
            </SuccessIconContainer>
            <SuccessTitle>Order Successful!</SuccessTitle>
            <SuccessMessage>
              Thank you for your purchase. Your order details are below.
            </SuccessMessage>
          </SuccessHeader>

          <MainContentWrapper>
            <LeftColumn>
              <CardContainer
                $bgColor="linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                $borderColor="#bbdefb"
              >
                <CardTitle $color="#1565c0">
                  <Savings /> Your Cashback Reward
                </CardTitle>
                {isLoading ? (
                  <CashbackAmount>
                    <CircularProgress
                      size={28}
                      style={{ color: "#1976d2", marginRight: "10px" }}
                    />
                    Calculating...
                  </CashbackAmount>
                ) : (
                  <CashbackAmount>
                    <Savings /> ₹{cashbackAmount.toFixed(2)}
                  </CashbackAmount>
                )}
                <CardDescription $color="#1e88e5">
                  This amount will be credited to your account within 24 hours
                </CardDescription>
              </CardContainer>

              <CardContainer
                $bgColor="linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)"
                $borderColor="#e1bee7"
              >
                <QuoteIcon $color="#7b1fa2" />
                <QuoteText>{quote}</QuoteText>
                <QuoteIcon
                  $color="#7b1fa2"
                  style={{ transform: "rotate(180deg)" }}
                />
              </CardContainer>

              <SectionTitle>
                <ShoppingBag /> Your Purchased Items
              </SectionTitle>

              {state.products.map((product, index) => (
                <ProductItem key={index}>
                  {product.Image1 ? (
                    <ProductImage
                      src={`${productImageStartingPath}/${product.Image1}`}
                      alt={product.ProductName}
                    />
                  ) : (
                    <ProductIcon>
                      <ShoppingBag style={{ color: "#9e9e9e" }} />
                    </ProductIcon>
                  )}
                  <ProductInfo>
                    <ProductName>
                      {product.ProductName || `Product ${product.ID}`}
                      {product.Mrp > product.OurPrice && (
                        <DiscountPill>
                          <LocalOffer style={{ fontSize: "14px" }} /> Save ₹
                          {(product.Mrp - product.OurPrice).toFixed(2)}
                        </DiscountPill>
                      )}
                    </ProductName>
                    <ProductPrice>
                      ₹{product.OurPrice || "0.00"} × {product.Quantity || 1} =
                      ₹{(product.OurPrice * (product.Quantity || 1)).toFixed(2)}
                      {product.Mrp > product.OurPrice && (
                        <span
                          style={{
                            textDecoration: "line-through",
                            color: "#9e9e9e",
                            marginLeft: "8px",
                          }}
                        >
                          ₹{(product.Mrp * (product.Quantity || 1)).toFixed(2)}
                        </span>
                      )}
                    </ProductPrice>
                    {productCashbacks[product.ID]?.totalCashback > 0 && (
                      <>
                        <ProductStatus>
                          <CheckCircle style={{ fontSize: "16px" }} /> Eligible
                          for cashback
                        </ProductStatus>
                        <ProductCashback>
                          <Savings style={{ fontSize: "16px" }} /> Cashback: ₹
                          {productCashbacks[
                            product.ID
                          ]?.perItemCashback?.toFixed(2)}{" "}
                          per item × {product.Quantity} = ₹
                          {productCashbacks[product.ID]?.totalCashback?.toFixed(
                            2
                          )}
                        </ProductCashback>
                      </>
                    )}
                  </ProductInfo>
                </ProductItem>
              ))}

              <TotalAmount>
                <span>Subtotal:</span>
                <span>₹{subTotal.toFixed(2)}</span>
              </TotalAmount>

              {totalSavings > 0 && (
                <TotalSavings>
                  <span>Total Savings:</span>
                  <span>
                    ₹{totalSavings.toFixed(2)}
                    <SavingsBadge>
                      <Savings style={{ fontSize: "16px" }} /> Great deal!
                    </SavingsBadge>
                  </span>
                </TotalSavings>
              )}

              <ContinueButton onClick={handleContinueShopping}>
                Continue Shopping
              </ContinueButton>
            </LeftColumn>

            <RightColumn>
              <OrderSummaryTitle>
                <ShoppingBag /> Order Summary
              </OrderSummaryTitle>

              <OrderSummaryItem>
                <OrderSummaryLabel>Order Number:</OrderSummaryLabel>
                <OrderSummaryValue>
                  {state.orderNumber || `ORD-${Date.now()}`}
                </OrderSummaryValue>
              </OrderSummaryItem>

              <OrderSummaryItem>
                <OrderSummaryLabel>Order Date:</OrderSummaryLabel>
                <OrderSummaryValue>
                  {new Date().toLocaleDateString()}
                </OrderSummaryValue>
              </OrderSummaryItem>

              <OrderSummaryItem>
                <OrderSummaryLabel>Items:</OrderSummaryLabel>
                <OrderSummaryValue>{state.products.length}</OrderSummaryValue>
              </OrderSummaryItem>

              <OrderSummaryItem>
                <OrderSummaryLabel>Subtotal:</OrderSummaryLabel>
                <OrderSummaryValue>₹{subTotal.toFixed(2)}</OrderSummaryValue>
              </OrderSummaryItem>

              {discountAmount > 0 && (
                <OrderSummaryItem>
                  <OrderSummaryLabel>
                    <LocalOffer style={{ fontSize: "18px" }} /> Discount:
                  </OrderSummaryLabel>
                  <OrderSummaryValue style={{ color: "#e91e63" }}>
                    -₹{discountAmount.toFixed(2)}
                  </OrderSummaryValue>
                </OrderSummaryItem>
              )}

              <OrderSummaryItem>
                <OrderSummaryLabel>Shipping:</OrderSummaryLabel>
                <OrderSummaryValue>FREE</OrderSummaryValue>
              </OrderSummaryItem>

              <OrderSummaryTotal>
                <span>Total:</span>
                <span>₹{subTotal.toFixed(2)}</span>
              </OrderSummaryTotal>

              {cashbackAmount > 0 && (
                <OrderSummaryItem>
                  <OrderSummaryLabel>
                    <Savings style={{ fontSize: "18px" }} /> Cashback Earned:
                  </OrderSummaryLabel>
                  <OrderSummaryValue
                    style={{ color: "#0d47a1", fontWeight: "700" }}
                  >
                    +₹{cashbackAmount.toFixed(2)}
                  </OrderSummaryValue>
                </OrderSummaryItem>
              )}

              <CardContainer
                $bgColor="linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
                $borderColor="#c8e6c9"
                style={{ marginTop: "25px" }}
              >
                <CardTitle $color="#2e7d32">
                  <CheckCircle /> Order Status
                </CardTitle>
                <CardDescription $color="#2e7d32">
                  Your order has been received and is being processed. You'll
                  receive a confirmation email shortly.
                </CardDescription>
              </CardContainer>
            </RightColumn>
          </MainContentWrapper>
        </SuccessContent>
      </SuccessScreenContainer>

      {state.products.length > 0 && (
        <RelatedProduct
          productId={state.products[0].ID || state.products[0].id}
        />
      )}

      <Footer />
    </>
  );
};

export default SuccessScreen;
